import { Button, Chip, Skeleton, Spinner } from '@heroui/react';
import { Copy, Download, FolderOpen, RefreshCw, Trash2 } from 'lucide-react';
import React, { FC, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useHeroToast } from '../../../../hooks';
import {
  AdCreativeListItem,
  useDeleteAdCreatives,
  useFetchAdCreative,
  useGetAdCreatives,
} from '../../../../queries';
import { findFormat, findPlatform } from '../../config/platforms';
import { useCreativeDownload } from '../../hooks';
import { useAdsGeneratorStore } from '../../model/store';
import { fromSavedCreative } from '../../utils/scene';
import { AdaptFormatsModal } from './AdaptFormatsModal';
import {
  CardHint,
  CardTitle,
  CreativeActions,
  CreativeCard,
  CreativeGrid,
  CreativePreview,
  FilterRow,
  StepDescription,
  StepHeader,
  StepHeading,
  StepLabel,
} from './PlatformStep.styled';

interface SavedCreativesTabProps {
  /** Wołane po wczytaniu kreacji do edytora — strona przenosi wtedy na jego adres. */
  onOpen?: () => void;
}

const ALL = 'all';

/** Nazwa parametru filtra w adresie, np. `?platform=facebook`. */
const PLATFORM_PARAM = 'platform';

/**
 * Gotowe kreacje z biblioteki, do otwarcia albo pobrania bez wchodzenia do
 * edytora. Plik powstaje z zapisanej sceny w pełnej rozdzielczości formatu:
 * miniatura widoczna na kafelku ma 480 px i służy tylko do rozpoznania kreacji.
 */
export const SavedCreativesTab: FC<SavedCreativesTabProps> = ({ onOpen }) => {
  const loadScene = useAdsGeneratorStore((state) => state.loadScene);
  const { data: creatives = [], isLoading, isFetching, refetch } = useGetAdCreatives('creative');
  const fetchCreative = useFetchAdCreative();
  const { mutate: removeCreatives } = useDeleteAdCreatives();
  const { download, downloadingId, stage } = useCreativeDownload();
  const { showError } = useHeroToast();

  // Filtr siedzi w adresie, więc „kreacje dla Facebooka" da się podlinkować
  // i wrócić do nich przyciskiem wstecz.
  const [adapting, setAdapting] = useState<AdCreativeListItem | null>(null);
  // Kasowanie idzie jedną mutacją dla całej listy, więc sam `isPending` nie
  // mówi, KTÓRY kafelek czeka — stąd id obok niego.
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const platformId = searchParams.get(PLATFORM_PARAM) ?? ALL;

  const setPlatformId = (next: string): void => {
    const params = new URLSearchParams(searchParams);
    if (next === ALL) params.delete(PLATFORM_PARAM);
    else params.set(PLATFORM_PARAM, next);
    setSearchParams(params, { replace: true });
  };

  /** Filtr pokazuje tylko platformy, dla których faktycznie coś zapisano. */
  const platforms = useMemo(() => {
    const ids = Array.from(new Set(creatives.map((item) => item.platformId)));
    return ids.map((id) => ({ id, name: findPlatform(id)?.name ?? id }));
  }, [creatives]);

  const visible = platformId === ALL
    ? creatives
    : creatives.filter((item) => item.platformId === platformId);

  const open = async (creative: AdCreativeListItem): Promise<void> => {
    try {
      const details = await fetchCreative(creative.id);
      loadScene(fromSavedCreative(details), { id: details.id, name: details.name, kind: 'creative' });
      onOpen?.();
    } catch (error) {
      showError(error instanceof Error ? error.message : 'Nie udało się otworzyć kreacji');
    }
  };

  const save = async (creative: AdCreativeListItem): Promise<void> => {
    try {
      const details = await fetchCreative(creative.id);
      await download(details);
    } catch (error) {
      console.error('[AdsGenerator] pobranie kreacji nie powiodło się', error);
      showError(error instanceof Error ? error.message : 'Nie udało się pobrać kreacji');
    }
  };

  return (
    <section>
      <StepHeader>
        <StepLabel>Biblioteka</StepLabel>
        <StepHeading>Moje kreacje</StepHeading>
        <StepDescription>
          Otwórz kreację do poprawek albo pobierz gotowy plik PNG w rozdzielczości formatu.
        </StepDescription>
      </StepHeader>

      <FilterRow>
        {isLoading && [80, 96, 72].map((width) => (
          <Skeleton key={width} className="h-6 rounded-md" style={{ width }} />
        ))}

        {!isLoading && (
        <Chip
          size="sm"
          radius="sm"
          variant={platformId === ALL ? 'solid' : 'flat'}
          className="cursor-pointer"
          onClick={() => setPlatformId(ALL)}
        >
          {`Wszystkie (${creatives.length})`}
        </Chip>
        )}
        {!isLoading && platforms.map((platform) => (
          <Chip
            key={platform.id}
            size="sm"
            radius="sm"
            variant={platformId === platform.id ? 'solid' : 'flat'}
            className="cursor-pointer"
            onClick={() => setPlatformId(platform.id)}
          >
            {platform.name}
          </Chip>
        ))}

        <Button
          size="sm"
          radius="sm"
          isIconOnly
          variant="light"
          aria-label="Odśwież listę kreacji"
          // Pierwsze ładowanie ma już swój wskaźnik niżej — tutaj kręci się
          // wyłącznie przy ręcznym odświeżeniu, żeby nie było dwóch naraz.
          isLoading={isFetching && !isLoading}
          onPress={() => refetch()}
        >
          <RefreshCw size={14} />
        </Button>
      </FilterRow>

      {isLoading && <Spinner size="sm" />}

      {!isLoading && visible.length === 0 && (
        <StepDescription>
          {creatives.length === 0
            ? 'Nie masz jeszcze zapisanych kreacji. Ułóż planszę w edytorze i zapisz ją przyciskiem Zapisz.'
            : 'Dla tej platformy nie ma jeszcze zapisanych kreacji.'}
        </StepDescription>
      )}

      <CreativeGrid>
        {visible.map((creative) => {
          const platform = findPlatform(creative.platformId);
          const format = findFormat(creative.platformId, creative.formatId);

          return (
            <CreativeCard key={creative.id}>
              <CreativePreview>
                {creative.preview
                  ? <img src={creative.preview} alt="" />
                  : <CardHint>Bez podglądu</CardHint>}
              </CreativePreview>
              <span>
                <CardTitle>{creative.name}</CardTitle>
                <CardHint>{`${platform?.name ?? creative.platformId}, ${format?.label ?? creative.formatId}`}</CardHint>
                <CardHint>{`${creative.width} x ${creative.height} px`}</CardHint>
              </span>
              <CreativeActions>
                <Button
                  size="sm"
                  radius="sm"
                  className="bg-black text-white"
                  startContent={<Download size={14} />}
                  isLoading={downloadingId === creative.id}
                  onPress={() => save(creative)}
                >
                  Pobierz
                </Button>
                <Button
                  size="sm"
                  radius="sm"
                  variant="flat"
                  isIconOnly
                  aria-label="Otwórz w edytorze"
                  title="Otwórz w edytorze"
                  onPress={() => open(creative)}
                >
                  <FolderOpen size={14} />
                </Button>
                <Button
                  size="sm"
                  radius="sm"
                  variant="flat"
                  isIconOnly
                  aria-label="Przelicz na inne formaty"
                  title="Przelicz na inne formaty"
                  onPress={() => setAdapting(creative)}
                >
                  <Copy size={14} />
                </Button>
                <Button
                  size="sm"
                  radius="sm"
                  variant="light"
                  color="danger"
                  isIconOnly
                  aria-label="Usuń kreację"
                  isLoading={deletingId === creative.id}
                  onPress={() => {
                    setDeletingId(creative.id);
                    removeCreatives({ ids: [creative.id] }, {
                      onError: (error) => showError(error.message || 'Nie udało się usunąć kreacji'),
                      onSettled: () => setDeletingId(null),
                    });
                  }}
                >
                  <Trash2 size={14} />
                </Button>
              </CreativeActions>
            </CreativeCard>
          );
        })}
      </CreativeGrid>

      {stage}

      {adapting && <AdaptFormatsModal creative={adapting} onClose={() => setAdapting(null)} />}
    </section>
  );
};
